/**
 * Invite Client to Portal Modal
 * Allows lawyers to invite clients to view their cases
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, Send, Loader2, CheckCircle, Copy, Building2, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

interface InviteClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId?: string;
  caseTitle?: string;
  prefillData?: {
    name?: string;
    email?: string;
    phone?: string;
    company_name?: string;
    client_id?: string;
  };
}

const InviteClientModal: React.FC<InviteClientModalProps> = ({
  isOpen,
  onClose,
  caseId,
  caseTitle,
  prefillData
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  
  const [formData, setFormData] = useState({
    name: prefillData?.name || '',
    email: prefillData?.email || '',
    phone: prefillData?.phone || '',
    company_name: prefillData?.company_name || '',
    send_email: true,
    send_sms: false,
    custom_message: ''
  });
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    
    setLoading(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/api/portal/invite`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company_name: formData.company_name || null,
        case_ids: caseId ? [caseId] : [],
        client_id: prefillData?.client_id || null,
        send_email: formData.send_email,
        send_sms: formData.send_sms,
        custom_message: formData.custom_message || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMagicLink(window.location.origin + response.data.magic_link);
      setSuccess(true);
      toast.success('Invitasjon sendt!');
    } catch (error: any) {
      console.error('Failed to invite client:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke sende invitasjon');
    } finally {
      setLoading(false);
    }
  };
  
  const copyLink = () => {
    navigator.clipboard.writeText(magicLink);
    toast.success('Lenke kopiert!');
  };
  
  const handleClose = () => {
    setSuccess(false);
    setMagicLink('');
    setFormData({
      name: prefillData?.name || '',
      email: prefillData?.email || '',
      phone: prefillData?.phone || '',
      company_name: prefillData?.company_name || '',
      send_email: true,
      send_sms: false,
      custom_message: ''
    });
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Inviter klient til portal</h2>
              {caseTitle && (
                <p className="text-sm text-gray-500 mt-0.5">Sak: {caseTitle}</p>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0 rounded-lg">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Invitasjon sendt!</h3>
                <p className="text-sm text-gray-500 mb-6">
                  {formData.name} vil motta en e-post med tilgang til portalen.
                </p>
                
                {/* Magic link for testing */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <Label className="text-xs text-gray-500 mb-2 block">Innloggingslenke (for testing)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={magicLink}
                      readOnly
                      className="text-xs font-mono bg-white"
                    />
                    <Button variant="outline" size="sm" onClick={copyLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <Button onClick={handleClose} className="w-full bg-gray-900 hover:bg-gray-800">
                  Ferdig
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Navn *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ola Nordmann"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                {/* Email */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">E-post *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="ola@example.no"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                {/* Phone (optional) */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Telefon (valgfritt)</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+47 123 45 678"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {/* Company (optional) */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Firma (valgfritt)</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Firma AS"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {/* Send options */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">Send invitasjon via</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.send_email}
                        onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">E-post</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.send_sms}
                        onChange={(e) => setFormData({ ...formData, send_sms: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">SMS</span>
                    </label>
                  </div>
                </div>
                
                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading || !formData.name || !formData.email}
                  className="w-full h-12 bg-gray-900 hover:bg-gray-800"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send invitasjon
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default InviteClientModal;
